import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
// TODO(convex-migration F4-5): trpc.admin.growth foi STUBADA. convex/admin.ts NÃO
// tem `growth` (per `.f3-12-report.md` é trabalho F3-9 — exige série temporal
// materializada via analytics indexes). GrowthChart renderiza vazio até port.
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Users,
  Shield,
  Clock,
  XCircle,
  CheckCircle,
  MoreVertical,
  Search,
  UserPlus,
  Activity,
  Ban,
  Trash2,
  Crown,
  RefreshCw,
  Zap,
  Eye,
  TrendingUp,
  Calendar,
} from "lucide-react";
import { toast } from "sonner";

interface AdminPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type FilterType = "all" | "trial" | "active" | "expired" | "admin";

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
    admin: { label: "Admin", className: "bg-violet-500/15 text-violet-400 border-violet-500/30", icon: <Crown className="h-3 w-3" /> },
    active: { label: "Ativo", className: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", icon: <CheckCircle className="h-3 w-3" /> },
    trial: { label: "Trial", className: "bg-amber-500/15 text-amber-400 border-amber-500/30", icon: <Clock className="h-3 w-3" /> },
    expired: { label: "Expirado", className: "bg-red-500/15 text-red-400 border-red-500/30", icon: <XCircle className="h-3 w-3" /> },
    canceled: { label: "Bloqueado", className: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30", icon: <Ban className="h-3 w-3" /> },
  };
  const c = config[status] || config.expired;
  return (
    <Badge variant="outline" className={`${c.className} gap-1 text-[10px] font-medium px-1.5 py-0`}>
      {c.icon} {c.label}
    </Badge>
  );
}

function StatCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: number; color: string }) {
  return (
    <div className={`rounded-lg border ${color} p-3 flex flex-col gap-1`}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <span className="text-2xl font-bold tracking-tight">{value}</span>
    </div>
  );
}

interface GrowthDataPoint {
  date: string;
  newUsers: number;
  cumulative: number;
  trial: number;
  active: number;
  expired: number;
}

function GrowthChart({ data, period, onPeriodChange, isLoading }: {
  data: GrowthDataPoint[];
  period: number;
  onPeriodChange: (p: number) => void;
  isLoading: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; point: GrowthDataPoint } | null>(null);

  const maxCumulative = useMemo(() => Math.max(...data.map(d => d.cumulative), 1), [data]);
  const maxNew = useMemo(() => Math.max(...data.map(d => d.newUsers), 1), [data]);
  const totalNewInPeriod = useMemo(() => data.reduce((s, d) => s + d.newUsers, 0), [data]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || data.length === 0) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = 180 * dpr;
    canvas.style.width = rect.width + "px";
    canvas.style.height = "180px";
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = 180;
    const padL = 40, padR = 12, padT = 12, padB = 28;
    const chartW = w - padL - padR;
    const chartH = h - padT - padB;

    ctx.clearRect(0, 0, w, h);

    // Grid lines
    ctx.strokeStyle = "rgba(255,255,255,0.04)";
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const y = padT + (chartH / 4) * i;
      ctx.beginPath();
      ctx.moveTo(padL, y);
      ctx.lineTo(w - padR, y);
      ctx.stroke();
    }

    // Y-axis labels
    ctx.fillStyle = "rgba(255,255,255,0.3)";
    ctx.font = "10px sans-serif";
    ctx.textAlign = "right";
    for (let i = 0; i <= 4; i++) {
      const y = padT + (chartH / 4) * i;
      const val = Math.round(maxCumulative * (1 - i / 4));
      ctx.fillText(String(val), padL - 6, y + 3);
    }

    // X-axis labels (show ~6 dates)
    ctx.textAlign = "center";
    ctx.fillStyle = "rgba(255,255,255,0.3)";
    const step = Math.max(1, Math.floor(data.length / 6));
    for (let i = 0; i < data.length; i += step) {
      const x = padL + (i / (data.length - 1)) * chartW;
      const parts = data[i].date.split("-");
      ctx.fillText(`${parts[2]}/${parts[1]}`, x, h - 6);
    }

    // Bar chart (new users)
    const barW = Math.max(2, (chartW / data.length) * 0.5);
    for (let i = 0; i < data.length; i++) {
      const x = padL + (i / Math.max(data.length - 1, 1)) * chartW;
      const barH = (data[i].newUsers / maxNew) * (chartH * 0.4);
      if (barH > 0) {
        const gradient = ctx.createLinearGradient(x, padT + chartH - barH, x, padT + chartH);
        gradient.addColorStop(0, "rgba(52, 211, 153, 0.6)");
        gradient.addColorStop(1, "rgba(52, 211, 153, 0.15)");
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.roundRect(x - barW / 2, padT + chartH - barH, barW, barH, [2, 2, 0, 0]);
        ctx.fill();
      }
    }

    // Cumulative line
    if (data.length > 1) {
      // Area fill
      ctx.beginPath();
      ctx.moveTo(padL, padT + chartH);
      for (let i = 0; i < data.length; i++) {
        const x = padL + (i / (data.length - 1)) * chartW;
        const y = padT + chartH - (data[i].cumulative / maxCumulative) * chartH;
        if (i === 0) ctx.lineTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.lineTo(padL + chartW, padT + chartH);
      ctx.closePath();
      const areaGrad = ctx.createLinearGradient(0, padT, 0, padT + chartH);
      areaGrad.addColorStop(0, "rgba(99, 102, 241, 0.15)");
      areaGrad.addColorStop(1, "rgba(99, 102, 241, 0.01)");
      ctx.fillStyle = areaGrad;
      ctx.fill();

      // Line
      ctx.beginPath();
      for (let i = 0; i < data.length; i++) {
        const x = padL + (i / (data.length - 1)) * chartW;
        const y = padT + chartH - (data[i].cumulative / maxCumulative) * chartH;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = "rgba(99, 102, 241, 0.9)";
      ctx.lineWidth = 2;
      ctx.lineJoin = "round";
      ctx.stroke();

      // Glow
      ctx.beginPath();
      for (let i = 0; i < data.length; i++) {
        const x = padL + (i / (data.length - 1)) * chartW;
        const y = padT + chartH - (data[i].cumulative / maxCumulative) * chartH;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = "rgba(99, 102, 241, 0.3)";
      ctx.lineWidth = 6;
      ctx.stroke();

      // Last point dot
      const lastX = padL + chartW;
      const lastY = padT + chartH - (data[data.length - 1].cumulative / maxCumulative) * chartH;
      ctx.beginPath();
      ctx.arc(lastX, lastY, 4, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(99, 102, 241, 1)";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(lastX, lastY, 7, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(99, 102, 241, 0.3)";
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }, [data, maxCumulative, maxNew]);

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || data.length === 0) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const padL = 40, padR = 12;
    const chartW = rect.width - padL - padR;
    const idx = Math.round(((mouseX - padL) / chartW) * (data.length - 1));
    if (idx >= 0 && idx < data.length) {
      setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, point: data[idx] });
    }
  };

  const periods = [
    { label: "7d", value: 7 },
    { label: "30d", value: 30 },
    { label: "90d", value: 90 },
    { label: "1a", value: 365 },
  ];

  return (
    <div className="rounded-lg border border-border/30 bg-card/50 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/20">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-indigo-400" />
          <span className="text-sm font-medium">Crescimento de Usuários</span>
          {totalNewInPeriod > 0 && (
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px] px-1.5 py-0">
              +{totalNewInPeriod} novos
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          {periods.map(p => (
            <button
              key={p.value}
              onClick={() => onPeriodChange(p.value)}
              className={`px-2 py-1 rounded text-[10px] font-medium transition-all ${
                period === p.value
                  ? "bg-indigo-500/15 text-indigo-400 border border-indigo-500/30"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pt-3 pb-2">
        {/* Legend */}
        <div className="flex items-center gap-4 mb-2">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 rounded bg-indigo-500" />
            <span className="text-[10px] text-muted-foreground">Total acumulado</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-sm bg-emerald-500/50" />
            <span className="text-[10px] text-muted-foreground">Novos por dia</span>
          </div>
        </div>

        {isLoading ? (
          <div className="h-[180px] rounded bg-muted/20 animate-pulse" />
        ) : (
          <div ref={containerRef} className="relative">
            <canvas
              ref={canvasRef}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setTooltip(null)}
              className="cursor-crosshair"
            />
            {tooltip && (
              <div
                className="absolute pointer-events-none z-10 bg-popover/95 backdrop-blur border border-border/50 rounded-lg px-3 py-2 shadow-xl"
                style={{
                  left: Math.min(tooltip.x, (containerRef.current?.clientWidth ?? 300) - 160),
                  top: Math.max(tooltip.y - 80, 0),
                }}
              >
                <p className="text-[10px] text-muted-foreground mb-1">
                  {new Date(tooltip.point.date + "T12:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}
                </p>
                <div className="space-y-0.5">
                  <p className="text-xs"><span className="text-indigo-400 font-medium">{tooltip.point.cumulative}</span> total</p>
                  <p className="text-xs"><span className="text-emerald-400 font-medium">+{tooltip.point.newUsers}</span> novos</p>
                  {tooltip.point.newUsers > 0 && (
                    <div className="flex gap-2 text-[10px] text-muted-foreground mt-0.5">
                      {tooltip.point.trial > 0 && <span>{tooltip.point.trial} trial</span>}
                      {tooltip.point.active > 0 && <span>{tooltip.point.active} ativos</span>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminPanel({ open, onOpenChange }: AdminPanelProps) {
  const [filter, setFilter] = useState<FilterType>("all");
  const [search, setSearch] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: Id<"users">; name: string } | null>(null);
  const [expandedUser, setExpandedUser] = useState<Id<"users"> | null>(null);
  const [isActioning, setIsActioning] = useState(false);

  const [growthPeriod, setGrowthPeriod] = useState(30);

  // Convex listUsers retorna { page, continueCursor, isDone } (paginado).
  // Reativo: sem refetch manual ao mutar.
  const usersQuery = useQuery(api.admin.listUsers, open ? {} : "skip");
  const stats = useQuery(api.admin.stats, open ? {} : "skip");
  // STUB: growth chart desativado até F3-9 portar api.admin.growth (série temporal
  // materializada via analytics indexes). Warn UMA vez por mount pra não poluir console.
  const growthWarnedRef = useRef(false);
  useEffect(() => {
    if (open && !growthWarnedRef.current) {
      growthWarnedRef.current = true;
      console.warn("[convex-migration] Analytics será reativado em breve (api.admin.growth pendente — F3-9)");
    }
  }, [open]);
  const growthQuery = {
    data: [] as GrowthDataPoint[],
    isLoading: false,
    refetch: () => {},
  };

  // Convex mutations — sem invalidate (reatividade automática).
  const updateUserMutation = useMutation(api.admin.updateUser);
  const extendTrialMutation = useMutation(api.admin.extendTrialDays);
  const deleteUserAction = useAction(api.admin.deleteUser);

  // Wrappers que mantêm a UX legada (toasts + isActioning flag).
  const wrap = async (op: () => Promise<unknown>, successMsg: string) => {
    setIsActioning(true);
    try {
      await op();
      toast.success(successMsg);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setIsActioning(false);
    }
  };

  const activateUser = (id: Id<"users">) =>
    wrap(() => updateUserMutation({ id, subscriptionStatus: "active" }), "Usuário ativado");
  const blockUser = (id: Id<"users">) =>
    wrap(() => updateUserMutation({ id, subscriptionStatus: "canceled" }), "Usuário bloqueado");
  const promoteAdmin = (id: Id<"users">) =>
    wrap(() => updateUserMutation({ id, role: "admin" }), "Usuário promovido a admin");
  const demoteAdmin = (id: Id<"users">) =>
    wrap(() => updateUserMutation({ id, role: "user" }), "Admin removido");
  const extendTrial = (id: Id<"users">, days: number) =>
    wrap(() => extendTrialMutation({ id, days }), `+${days} dias de trial`);

  const users = usersQuery?.page ?? [];
  const isLoading = usersQuery === undefined;
  const isFetching = isLoading;

  const filteredUsers = users.filter((u: any) => {
    if (filter !== "all" && u.effectiveStatus !== filter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
    }
    return true;
  });

  const filterButtons: { key: FilterType; label: string; count: number | undefined }[] = [
    { key: "all", label: "Todos", count: stats?.total },
    { key: "trial", label: "Trial", count: stats?.trial },
    { key: "active", label: "Ativos", count: stats?.active },
    { key: "expired", label: "Expirados", count: stats?.expired },
    { key: "admin", label: "Admins", count: stats?.admins ?? users.filter((u: any) => u.role === "admin").length },
  ];

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-xl md:max-w-2xl overflow-y-auto p-0 bg-background border-l border-border/50">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/50 sticky top-0 bg-background/95 backdrop-blur z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-violet-400" />
                </div>
                <div>
                  <SheetTitle className="text-base font-semibold">Painel Admin</SheetTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">Gerenciar usuários e acessos</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => { growthQuery.refetch(); /* Convex queries são reativas — admin.listUsers e admin.stats refetcham sozinhas */ }}
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </SheetHeader>

          <div className="p-6 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard
                icon={<Users className="h-3.5 w-3.5" />}
                label="Total"
                value={stats?.total ?? 0}
                color="border-blue-500/20 bg-blue-500/5"
              />
              <StatCard
                icon={<Clock className="h-3.5 w-3.5" />}
                label="Em Trial"
                value={stats?.trial ?? 0}
                color="border-amber-500/20 bg-amber-500/5"
              />
              <StatCard
                icon={<CheckCircle className="h-3.5 w-3.5" />}
                label="Ativos"
                value={stats?.active ?? 0}
                color="border-emerald-500/20 bg-emerald-500/5"
              />
              <StatCard
                icon={<UserPlus className="h-3.5 w-3.5" />}
                label="Novos Hoje"
                value={stats?.newToday ?? 0}
                color="border-cyan-500/20 bg-cyan-500/5"
              />
            </div>

            {/* Growth Chart */}
            <GrowthChart data={growthQuery.data ?? []} period={growthPeriod} onPeriodChange={setGrowthPeriod} isLoading={growthQuery.isLoading} />

            {/* Filters + Search */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 overflow-x-auto pb-1">
                {filterButtons.map(f => (
                  <button
                    key={f.key}
                    onClick={() => setFilter(f.key)}
                    className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      filter === f.key
                        ? "bg-primary/15 text-primary border border-primary/30"
                        : "bg-muted/50 text-muted-foreground hover:bg-muted border border-transparent"
                    }`}
                  >
                    {f.label}
                    {f.count !== undefined && (
                      <span className="ml-1.5 text-[10px] opacity-70">({f.count})</span>
                    )}
                  </button>
                ))}
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou email..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 h-9 text-sm bg-muted/30 border-border/50"
                />
              </div>
            </div>

            {/* Users List */}
            <div className="space-y-2">
              {isLoading ? (
                <div className="space-y-2">
                  {[1,2,3].map(i => (
                    <div key={i} className="h-16 rounded-lg bg-muted/30 animate-pulse" />
                  ))}
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nenhum usuário encontrado</p>
                </div>
              ) : (
                filteredUsers.map((user: any) => (
                  <UserRow
                    key={user._id}
                    user={user}
                    expanded={expandedUser === user._id}
                    onToggleExpand={() => setExpandedUser(expandedUser === user._id ? null : user._id)}
                    onActivate={() => activateUser(user._id)}
                    onBlock={() => blockUser(user._id)}
                    onExtendTrial={(days) => extendTrial(user._id, days)}
                    onPromoteAdmin={() => promoteAdmin(user._id)}
                    onDemoteAdmin={() => demoteAdmin(user._id)}
                    onDelete={() => setDeleteConfirm({ id: user._id, name: user.name || user.email || "Usuário" })}
                    isActioning={isActioning}
                  />
                ))
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deletar usuário?</AlertDialogTitle>
            <AlertDialogDescription>
              Isso vai remover permanentemente <strong>{deleteConfirm?.name}</strong> e todos os dados associados (campanhas, chat, configurações, vendas, leads). Essa ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                if (!deleteConfirm) return;
                setIsActioning(true);
                try {
                  await deleteUserAction({ id: deleteConfirm.id });
                  toast.success("Usuário removido");
                  setDeleteConfirm(null);
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : String(err));
                } finally {
                  setIsActioning(false);
                }
              }}
            >
              {isActioning ? "Deletando..." : "Deletar permanentemente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface UserRowProps {
  // Shape from api.admin.listUsers — Convex docs usam _id + _creationTime,
  // e `lastSignedIn` é um epoch ms (number) no schema users.
  user: {
    _id: Id<"users">;
    _creationTime: number;
    name?: string | null;
    email?: string | null;
    role: string;
    subscriptionStatus: string;
    effectiveStatus: string;
    trialDaysLeft: number;
    isTrialExpired: boolean;
    onboardingCompleted?: boolean | number;
    lastSignedIn?: number;
    loginMethod?: string | null;
  };
  expanded: boolean;
  onToggleExpand: () => void;
  onActivate: () => void;
  onBlock: () => void;
  onExtendTrial: (days: number) => void;
  onPromoteAdmin: () => void;
  onDemoteAdmin: () => void;
  onDelete: () => void;
  isActioning: boolean;
}

function UserRow({ user, expanded, onToggleExpand, onActivate, onBlock, onExtendTrial, onPromoteAdmin, onDemoteAdmin, onDelete, isActioning }: UserRowProps) {
  // api.admin.getUserDetails substitui getUserMeta + retorna { user, metaConfigs, ... }
  const detailsQuery = useQuery(
    api.admin.getUserDetails,
    expanded ? { id: user._id } : "skip",
  );
  // Pega 1ª metaConfig pra exibir resumo (legado mostrava 1 só).
  const metaData = detailsQuery?.metaConfigs?.[0] ?? null;
  const metaIsLoading = expanded && detailsQuery === undefined;

  const isAdmin = user.role === "admin";
  const createdDate = new Date(user._creationTime);
  const lastSeen = user.lastSignedIn ? new Date(user.lastSignedIn) : createdDate;
  const timeSinceLastSeen = getTimeAgo(lastSeen);

  return (
    <div className={`rounded-lg border transition-all ${expanded ? "border-primary/30 bg-primary/5" : "border-border/30 bg-card/50 hover:border-border/50"}`}>
      {/* Main Row */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Avatar */}
        <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${
          isAdmin ? "bg-violet-500/20 text-violet-400" : "bg-primary/10 text-primary"
        }`}>
          {user.name?.charAt(0)?.toUpperCase() || "?"}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">{user.name || "Sem nome"}</span>
            <StatusBadge status={user.effectiveStatus} />
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-muted-foreground truncate">{user.email || "Sem email"}</span>
            <span className="text-[10px] text-muted-foreground/60">·</span>
            <span className="text-[10px] text-muted-foreground/60 shrink-0">{timeSinceLastSeen}</span>
          </div>
        </div>

        {/* Trial Days */}
        {user.effectiveStatus === "trial" && (
          <div className="shrink-0 text-right">
            <span className={`text-xs font-medium ${user.trialDaysLeft <= 3 ? "text-red-400" : "text-amber-400"}`}>
              {user.trialDaysLeft}d
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onToggleExpand}
          >
            <Eye className="h-3.5 w-3.5" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={isActioning}>
                <MoreVertical className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {user.effectiveStatus !== "active" && !isAdmin && (
                <DropdownMenuItem onClick={onActivate} className="cursor-pointer">
                  <Zap className="mr-2 h-4 w-4 text-emerald-400" />
                  <span>Ativar acesso</span>
                </DropdownMenuItem>
              )}
              {(user.effectiveStatus === "trial" || user.effectiveStatus === "expired") && (
                <>
                  <DropdownMenuItem onClick={() => onExtendTrial(7)} className="cursor-pointer">
                    <Clock className="mr-2 h-4 w-4 text-amber-400" />
                    <span>+7 dias de trial</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onExtendTrial(14)} className="cursor-pointer">
                    <Clock className="mr-2 h-4 w-4 text-amber-400" />
                    <span>+14 dias de trial</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onExtendTrial(30)} className="cursor-pointer">
                    <Clock className="mr-2 h-4 w-4 text-amber-400" />
                    <span>+30 dias de trial</span>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              {!isAdmin ? (
                <DropdownMenuItem onClick={onPromoteAdmin} className="cursor-pointer">
                  <Crown className="mr-2 h-4 w-4 text-violet-400" />
                  <span>Promover a admin</span>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={onDemoteAdmin} className="cursor-pointer">
                  <Shield className="mr-2 h-4 w-4" />
                  <span>Remover admin</span>
                </DropdownMenuItem>
              )}
              {user.effectiveStatus !== "canceled" && !isAdmin && (
                <DropdownMenuItem onClick={onBlock} className="cursor-pointer text-amber-400">
                  <Ban className="mr-2 h-4 w-4" />
                  <span>Bloquear</span>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="cursor-pointer text-destructive focus:text-destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                <span>Deletar usuário</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-border/20 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <span className="text-muted-foreground">Cadastro</span>
              <p className="font-medium mt-0.5">{createdDate.toLocaleDateString("pt-BR")} às {createdDate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Último acesso</span>
              <p className="font-medium mt-0.5">{lastSeen.toLocaleDateString("pt-BR")} às {lastSeen.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Login via</span>
              <p className="font-medium mt-0.5">{user.loginMethod || "OAuth"}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Onboarding</span>
              <p className="font-medium mt-0.5">{user.onboardingCompleted ? "✓ Completo" : "✗ Pendente"}</p>
            </div>
          </div>

          {/* Meta Config — getUserDetails.metaConfigs[0] */}
          {metaIsLoading ? (
            <div className="h-12 rounded-lg bg-muted/30 animate-pulse" />
          ) : metaData ? (
            <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="h-3.5 w-3.5 text-blue-400" />
                <span className="text-xs font-medium text-blue-400">Meta Ads Conectado</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Conta</span>
                  <p className="font-medium mt-0.5 truncate">{metaData.adAccountName || metaData.adAccountId}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Expira em</span>
                  <p className="font-medium mt-0.5">
                    {metaData.tokenExpiresAt ? new Date(metaData.tokenExpiresAt).toLocaleDateString("pt-BR") : "—"}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Autopilot</span>
                  <p className="font-medium mt-0.5">{metaData.autopilotEnabled ? "✓ Ativo" : "✗ Desativado"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Status</span>
                  <p className="font-medium mt-0.5">{metaData.isActive ? "✓ Ativo" : "✗ Inativo"}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-border/20 bg-muted/20 p-3 text-center">
              <span className="text-xs text-muted-foreground">Meta Ads não configurado</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "agora";
  if (diffMins < 60) return `${diffMins}min atrás`;
  if (diffHours < 24) return `${diffHours}h atrás`;
  if (diffDays < 7) return `${diffDays}d atrás`;
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}
